import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, notifyError } from "../lib/api";
import { fmtMoney } from "../lib/currency";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Users,
  PackageOpen,
  Wallet,
  BellRing,
  ArrowUpRight,
  PackageCheck,
  TrendingUp,
  AlertCircle,
  LifeBuoy,
  Globe,
  Mail,
  Phone,
  CreditCard,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import WhatsAppIcon from "../components/WhatsAppIcon";
import HelpVideosButton from "../components/HelpVideosDialog";

function StatCard({ label, value, icon: Icon, hint, accent, testId }) {
  return (
    <Card className="surface-card p-5 sm:p-6 rounded-sm" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className={`h-9 w-9 rounded-sm flex items-center justify-center ${accent ? "bg-amber-500 text-white" : "bg-foreground text-background"}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-4">
        {value}
      </div>
      {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
    </Card>
  );
}

/**
 * Sanal POS Ön Başvurusu (şartname #4).
 *
 * Akış:
 *   1. Butona basılır → `/pos-applications/eligibility` kontrol edilir.
 *   2. Fatura bilgileri eksikse önce onların girilmesi istenir (Personel
 *      ekranındaki yönetici profili) — başvuru formu açılmaz.
 *   3. Bilgiler tamsa aylık tahmini ciro istenir ve başvuru kaydedilir.
 *   4. Şartnamedeki teşekkür mesajı gösterilir.
 *
 * BAŞVURU YAPILDIYSA KART GÖSTERİLMEZ: panoya her girişte `/eligibility`
 * okunur; `has_application` true ise (ya da bu oturumda başvuru tamamlandıysa)
 * kart tamamen kaldırılır — aynı işletme ikinci kez ön başvuru yapmaz.
 */
function PosApplicationCard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [volume, setVolume] = useState("");
  const [done, setDone] = useState(false);
  // null → henüz bilinmiyor (kart çizilmez), true/false → sonuç belli.
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/pos-applications/eligibility");
        if (cancelled) return;
        setEligibility(data);
        setApplied(!!data.has_application);
      } catch {
        // Sessiz geç: uygunluk okunamazsa kart gizli kalır, pano bozulmaz.
        if (!cancelled) setApplied(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const start = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/pos-applications/eligibility");
      setEligibility(data);
      if (data.has_application) {
        // Başka bir sekmeden/kullanıcıdan başvuru yapılmış olabilir.
        setApplied(true);
        return;
      }
      setDone(false);
      setVolume("");
      setOpen(true);
    } catch (e) {
      notifyError(e);
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(volume);
    if (!amount || amount <= 0) {
      toast.error("Aylık tahmini ciro tutarını girin");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/pos-applications", { monthly_volume: amount });
      setDone(true);
      // Diyalog kapandığında kart bir daha gösterilmez.
      setApplied(true);
    } catch (err) {
      notifyError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const invoiceReady = eligibility?.invoice_ready;

  // Başvuru zaten yapılmışsa kart görünmez. Teşekkür mesajı açık kaldığı
  // sürece diyalog kapanana kadar bekler.
  if (applied !== false && !open) return null;

  return (
    <>
      <Card
        className="rounded-sm border-border p-6 flex flex-wrap items-center gap-4"
        data-testid="pos-application-card"
      >
        <div className="h-12 w-12 bg-foreground text-amber-500 flex items-center justify-center rounded-sm shrink-0">
          <CreditCard className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-bold text-lg tracking-tight">
            Sanal POS Başvurusu
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Sistem üzerinden Satış/Stok takibi yapabilir ve ilgili ekrandan sanal
            pos hesabınız ile tahsilat gerçekleştirebilirsiniz.
          </p>
        </div>
        <Button
          onClick={start}
          disabled={checking}
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
          data-testid="pos-application-btn"
        >
          {checking ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Ön Başvuru İçin Tıklayın"
          )}
        </Button>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="pos-application-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Sanal POS Başvurusu
            </DialogTitle>
            {!done && (
              <DialogDescription>
                {invoiceReady
                  ? "Sanal POS ile aylık gerçekleştirmeyi öngördüğünüz tahmini ciro tutarını girin."
                  : "Başvuru için önce firma fatura bilgilerinizi tamamlamanız gerekiyor."}
              </DialogDescription>
            )}
          </DialogHeader>

          {done ? (
            <div
              className="flex items-start gap-3 border-l-2 border-emerald-500 bg-emerald-50 px-4 py-4 text-sm text-emerald-900"
              data-testid="pos-application-success"
            >
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
              <p>
                Ön başvurunuz alındı. Teşekkür ederiz. Diğer bilgiler ile ilgili
                kısa sürede size ulaşacağız.
              </p>
            </div>
          ) : !invoiceReady ? (
            <div className="space-y-3">
              <div
                className="border-l-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                data-testid="pos-application-invoice-required"
              >
                <div className="font-semibold">Fatura bilgileri eksik</div>
                <ul className="mt-2 list-disc pl-5 space-y-0.5">
                  {(eligibility?.missing_fields || []).map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-muted-foreground">
                Fatura bilgilerini <b>Personel</b> ekranından yönetici kaydınızı
                düzenleyerek girebilirsiniz.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="pos-application-form">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Aylık Tahmini Ciro *</Label>
                {/* İpucu alanın ÜSTÜNDE: kullanıcı tutarı yazmadan önce ne
                    beklendiğini görsün. */}
                <p className="text-[11px] text-muted-foreground">
                  Sanal POS üzerinden aylık gerçekleştirmeyi öngördüğünüz toplam tutar.
                </p>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="1"
                  step="0.01"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  placeholder="Örn. 250000"
                  className="rounded-sm font-mono"
                  data-testid="pos-application-volume"
                  required
                />
              </div>
            </form>
          )}

          <DialogFooter>
            {done ? (
              <Button
                onClick={() => setOpen(false)}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                data-testid="pos-application-close"
              >
                Kapat
              </Button>
            ) : !invoiceReady ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                  Vazgeç
                </Button>
                <Button
                  onClick={() => {
                    setOpen(false);
                    navigate("/personel");
                  }}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid="pos-application-goto-invoice"
                >
                  Fatura Bilgilerini Gir
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                  Vazgeç
                </Button>
                <Button
                  onClick={submit}
                  disabled={submitting || !volume}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid="pos-application-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Başvuruyu Gönder"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Dashboard() {
  const { defaultCurrency, isOwner, tenantSettings } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, rem] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/storages?status=depoda"),
          api.get("/reminders?days=30"),
        ]);
        setStats(s.data);
        setRecent(r.data.slice(0, 5));
        setReminders(rem.data.slice(0, 5));
      } catch (e) {
        notifyError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Genel Durum</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-2">
            Pano
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-md">
            Lastik otelinizdeki tüm hareketlerin canlı özeti.
          </p>
          {tenantSettings?.has_demo_data && (
            <p
              className="text-xs text-amber-700 mt-2 max-w-md"
              data-testid="demo-data-hint"
            >
              Demo verilerini,{" "}
              <Link to="/ayarlar" className="underline font-semibold hover:text-amber-800">
                ayarlar ekranından
              </Link>{" "}
              temizleyebilirsiniz.
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <Button asChild variant="outline" className="rounded-sm" data-testid="quick-add-customer-btn">
            <Link to="/musteriler">+ Müşteri</Link>
          </Button>
          <Button asChild className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="quick-add-storage-btn">
            <Link to="/emanetler">+ Yeni Emanet</Link>
          </Button>
        </div>
      </div>

      {/* Subscription Warning */}
      {!loading && stats?.subscription && stats.subscription.days_remaining !== null && stats.subscription.days_remaining <= 7 && (
        <Alert
          className="rounded-sm border-amber-500/50 bg-amber-50 dark:bg-amber-950/30"
          data-testid="subscription-warning-alert"
        >
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900 dark:text-amber-100">Abonelik Süresi Sona Yaklaşıyor</AlertTitle>
          <AlertDescription className="text-amber-800 dark:text-amber-100/80 mt-2">
            <div className="mb-3">
              <strong>Kalan gün: {stats.subscription.days_remaining}</strong>
            </div>
            <p className="mb-4">Lütfen abonelik planınızı güncelleyin.</p>
            {isOwner && (
              <Button
                asChild
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                data-testid="subscription-update-btn"
              >
                <Link to="/abonelik">Abonelik Planını Güncelle</Link>
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Subscription Expired — Grace Period Banner */}
      {!loading && stats?.subscription && stats.subscription.is_expired && (
        <Alert
          className="rounded-sm border-destructive/50 bg-destructive/10"
          data-testid="subscription-expired-alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive" />
          <AlertTitle className="text-destructive">Sistem Kullanım Süreniz Bitti</AlertTitle>
          <AlertDescription className="text-destructive/90 mt-2">
            <p className="mb-4">
              Sistem Kullanım süreniz bitti. 3 gün ek süre tanımlanmıştır. Kesintiye uğramamak için hemen paketinizi güncelleyin.
            </p>
            {isOwner && (
              <Button
                asChild
                className="bg-destructive hover:bg-destructive/90 text-white rounded-sm"
                data-testid="subscription-expired-update-btn"
              >
                <Link to="/abonelik">Paketi Hemen Güncelle</Link>
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-sm" />
          ))}
        </div>
      ) : !stats ? (
        <Card className="rounded-sm border-border p-6 text-sm text-muted-foreground">
          İstatistikler yüklenemedi.
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Toplam Müşteri"
            value={stats.total_customers}
            icon={Users}
            hint="kayıtlı"
            testId="stat-customers"
          />
          <StatCard
            label="Aktif Emanet"
            value={stats.active_storages}
            icon={PackageOpen}
            hint="depoda"
            accent
            testId="stat-active"
          />
          <StatCard
            label="Bekleyen Ödeme"
            value={fmtMoney(stats.outstanding_amount, defaultCurrency, { maximumFractionDigits: 0 })}
            icon={Wallet}
            hint="toplam"
            testId="stat-outstanding"
          />
          <StatCard
            label="Yaklaşan Hatırlatma"
            value={stats.upcoming_reminders}
            icon={BellRing}
            hint="30 gün içinde"
            testId="stat-reminders"
          />
        </div>
      )}

      {/* Sanal POS ön başvurusu — şartname #4 (yalnızca yönetici) */}
      {isOwner && <PosApplicationCard />}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent storages */}
        <Card className="rounded-sm border-border">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div>
              <div className="label-eyebrow">Son Emanetler</div>
              <h3 className="font-display font-bold text-xl tracking-tight mt-1">Depodakiler</h3>
            </div>
            <Button asChild variant="ghost" size="sm" className="rounded-sm">
              <Link to="/emanetler" className="flex items-center gap-1">
                Tümü <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
          <div className="divide-y divide-border">
            {recent.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Henüz aktif emanet yok.
              </div>
            )}
            {recent.map((s) => (
              <Link
                key={s.id}
                to={`/emanetler/${s.id}`}
                className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                data-testid={`recent-storage-${s.id}`}
              >
                <div className="h-10 w-10 bg-foreground text-background flex items-center justify-center rounded-sm shrink-0">
                  <PackageCheck className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{s.customer_name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono truncate">
                    {s.brand} • {s.size} • {s.quantity} adet
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-600">
                    {s.season === "yaz" ? "YAZ" : s.season === "kis" ? "KIŞ" : "4 MEV"}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {s.warehouse_name || "—"} • {s.location || "raf yok"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>

        {/* Reminders */}
        <Card className="rounded-sm border-border">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div>
              <div className="label-eyebrow text-amber-600">Yaklaşan</div>
              <h3 className="font-display font-bold text-xl tracking-tight mt-1">Sezon Değişimi</h3>
            </div>
            <Button asChild variant="ghost" size="sm" className="rounded-sm">
              <Link to="/hatirlatmalar" className="flex items-center gap-1">
                Tümü <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
          <div className="divide-y divide-border">
            {reminders.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Yaklaşan hatırlatma yok.
              </div>
            )}
            {reminders.map((s) => (
              <Link
                key={s.id}
                to={`/emanetler/${s.id}`}
                className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                data-testid={`reminder-${s.id}`}
              >
                <div className="h-10 w-10 bg-amber-500 text-foreground flex items-center justify-center rounded-sm shrink-0">
                  <BellRing className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{s.customer_name}</div>
                  <div className="text-xs text-muted-foreground truncate font-mono">
                    {s.plate || s.customer_phone}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-mono">
                    {s.expected_pickup_date
                      ? new Date(s.expected_pickup_date).toLocaleDateString("tr-TR")
                      : "—"}
                  </div>
                  <div className="text-xs text-amber-600 font-bold uppercase">Bilgilendir</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {/* Footer signal */}
      <Card className="rounded-sm border-border bg-foreground text-background p-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-amber-500 text-foreground flex items-center justify-center rounded-sm">
            <TrendingUp className="h-6 w-6" />
          </div>
          <div>
            <div className="label-eyebrow text-amber-400">İPUCU</div>
            <div className="font-display font-bold text-lg tracking-tight mt-1">
              Sezon başlangıcında müşterilerinize hatırlatma gönderin
            </div>
            <div className="text-sm text-background/60 mt-1">
              Hatırlatmalar sayfasından yaklaşan teslim tarihlerini görüntüleyebilirsiniz.
            </div>
          </div>
        </div>
      </Card>

      {/* Support info */}
      <Card className="rounded-sm border-border p-6" data-testid="support-info">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 bg-amber-500 text-foreground flex items-center justify-center rounded-sm shrink-0">
            <LifeBuoy className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="label-eyebrow text-amber-600">Bilgi ve Destek için</div>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <a
                href="https://www.esnekyazilim.com.tr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-semibold hover:text-amber-600 transition-colors"
              >
                <Globe className="h-4 w-4 text-amber-600" />
                www.esnekyazilim.com.tr
              </a>
              <a
                href="mailto:info@esnekyazilim.com.tr"
                className="inline-flex items-center gap-2 font-mono hover:text-amber-600 transition-colors"
              >
                <Mail className="h-4 w-4 text-amber-600" />
                info@esnekyazilim.com.tr
              </a>
              <a
                href="tel:+905317932454"
                className="inline-flex items-center gap-2 font-mono hover:text-amber-600 transition-colors"
              >
                <Phone className="h-4 w-4 text-amber-600" />
                +90 531 793 24 54
              </a>
              <a
                href="https://wa.me/905317932454"
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp ile yaz"
                className="inline-flex items-center gap-2 font-mono text-emerald-700 hover:text-emerald-800 transition-colors"
              >
                <WhatsAppIcon className="h-4 w-4" />
                +90 531 793 24 54
              </a>
              {/* Yardım videoları — Ayarlar'daki kartla aynı bileşen */}
              <HelpVideosButton />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
